import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, NavigationExtras, Router } from '@angular/router';

const APP_LOGIN_DEEP_LINK = 'mymasjid://login';

// The posts/events screens are mounted twice: under /dashboard (sidebar layout) and at the root
// as "embedded" routes the mobile app opens in its webview (no sidebar, the app supplies its own
// header). Embedded routes are marked with `data: { embedded: true }` in app.routes.ts; screens ask
// this service which shell they are in instead of inspecting the URL.
@Injectable({
  providedIn: 'root'
})
export class AppShellService {
  private readonly router = inject(Router);

  get isEmbedded(): boolean {
    let route: ActivatedRouteSnapshot | null = this.router.routerState?.snapshot.root ?? null;
    while (route) {
      if (route.data['embedded']) {
        return true;
      }
      route = route.firstChild;
    }
    return false;
  }

  get wrapperClass(): string {
    return this.isEmbedded ? 'wrapper' : 'page-wrapper';
  }

  // Navigates to a screen in the current shell. `commands` are relative to the shell root, e.g.
  // ['edit-post', id, masjidId] → /edit-post/... in the webview, /dashboard/edit-post/... otherwise.
  // Webview navigations keep the query params the app opened the page with.
  navigate(commands: any[], extras: NavigationExtras = {}): Promise<boolean> {
    if (this.isEmbedded) {
      return this.router.navigate(['/', ...commands], { queryParamsHandling: 'merge', ...extras });
    }
    return this.router.navigate(['/dashboard', ...commands], extras);
  }

  // Sends a signed-out user to log in again. Inside the app webview that is the app's own login:
  // the deep link hands control back to the app, which closes the webview. `embedded` can be passed
  // by callers that run before the current route is active (e.g. guards).
  redirectToLogin(returnUrl?: string, embedded = this.isEmbedded): void {
    if (embedded) {
      window.location.href = APP_LOGIN_DEEP_LINK;
      return;
    }
    if (returnUrl) {
      this.router.navigate(['/login'], { queryParams: { returnUrl } });
    } else {
      this.router.navigate(['/login']);
    }
  }
}
